import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Hash, Target, Trophy, Star } from "lucide-react";

const API = '/api';

export default function SearchResults() {
  const { getAuthHeader } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setSearching(true);
    try {
      const res = await axios.get(`${API}/search/results?q=${encodeURIComponent(query)}`);
      setResults(res.data);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="search-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
          Search Results
        </h1>
        <p className="text-slate-500 mt-1">Search by Shooter ID or Match Number</p>
      </div>

      {/* Search Form */}
      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter Shooter ID or Match Number..."
                className="text-lg"
                data-testid="search-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={searching}
              className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
              data-testid="search-btn"
            >
              <Search size={20} className="mr-2" />
              {searching ? "Searching..." : "Search"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <div className="space-y-6">
          {/* Shooter Results */}
          {results.shooter_results.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Hash className="text-[#1B3B6F]" />
                  Shooters Found ({results.shooter_results.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {results.shooter_results.map((item) => (
                    <div key={item.shooter.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                            ID: {item.shooter.shooter_id}
                          </span>
                          <h3 className="font-semibold text-lg mt-1">{item.shooter.name}</h3>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-slate-500">Best Score</div>
                          <div className="font-mono text-2xl font-bold text-green-700">{item.best_score}</div>
                        </div>
                      </div>
                      <div className="text-sm text-slate-600">
                        <span>{item.total_rounds} round(s) recorded</span>
                        <span className="mx-2">|</span>
                        <span>Best Streak: {item.best_streak}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Match Results */}
          {results.match_results.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Target className="text-[#1B3B6F]" />
                  Matches Found ({results.match_results.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {results.match_results.map((item) => (
                    <div key={item.match.id} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        {item.match.match_number && (
                          <Badge variant="outline" className="font-mono">
                            #{item.match.match_number}
                          </Badge>
                        )}
                        <h3 className="font-semibold text-lg">{item.match.name}</h3>
                      </div>
                      
                      {/* Shooters in this match */}
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Shooter ID</th>
                            <th>Name</th>
                            <th>Rounds</th>
                            <th>Best Score</th>
                            <th>Best Streak</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.shooters.map((shooter, idx) => (
                            <tr key={idx}>
                              <td className="font-mono text-sm">
                                {shooter.shooter_id_number || "-"}
                              </td>
                              <td className="font-medium">{shooter.shooter_name}</td>
                              <td>
                                {shooter.rounds.map((round, ridx) => (
                                  <span key={ridx} className="inline-flex items-center mr-2">
                                    <span className={round.is_best_round ? "font-bold text-green-700" : ""}>
                                      {round.total_hits}
                                    </span>
                                    {round.is_best_round && (
                                      <Star size={12} className="ml-1 text-yellow-500 fill-yellow-500" />
                                    )}
                                  </span>
                                ))}
                              </td>
                              <td className="font-mono font-bold text-green-700">{shooter.best_score}</td>
                              <td className="font-mono">{shooter.best_streak}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Results */}
          {results.shooter_results.length === 0 && results.match_results.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Search className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-slate-500">No results found for "{query}"</p>
                <p className="text-sm text-slate-400 mt-2">Try searching with a different Shooter ID or Match Number</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
